/* ============================================================================
 * Panels — design languages, modelled as a lineage
 * ----------------------------------------------------------------------------
 * A panel is a design language applied as `data-skin` on `<html>`. Nothing in
 * the component stylesheet knows which panel is active: every colour, radius,
 * bevel, border width, font and metric is read from the semantic `--ui-*` layer
 * that each panel populates. (Panels were formerly called "skins"/"pages" —
 * same mechanism, new name.)
 *
 *   oceanside       MASTER      the styleless chassis (Nude Build). Structure
 *                               only — nothing to style. Sits on `:root`.
 *   supercompute    fork        Terminal Dossier — navy, brass-gold, mono.
 *   poolside        fork        retro-80s vacation (was `poolsuite`).
 *   poolside-web3   fork        Poolside with Web3 rails — not yet built.
 *   school          fork        green ground, white ink, gold accent.
 *   community-club  fork        club fork, community (split from `club-manager`).
 *   athletic-club   fork        club fork, athletic   (split from `club-manager`).
 *   world-market    fork        distributed flea-market IP — not yet built.
 *   idea-club       fork        not yet built.
 *   oceanside-os    structural  native macOS/Aqua — owns geometry, type, chrome.
 *
 * ROLE VOCABULARY — the settled four, exactly (DESIGN.md §11):
 *   `master` · `fork` · `structural` · `legacy`.
 * `panel` is the PRODUCT NOUN (the surface a person switches to), never a role;
 * `origin` is not a role either — Poolside's retro history is a note on its row.
 * A `structural` fork (DESIGN.md §2.1) is the gated class that may own geometry,
 * type and chrome; it ships committed CSS, never runtime forge data.
 *
 * Only the master sits on `:root`, so a surface with no `data-skin` renders the
 * chassis. Every panel declares `forksFrom`, so lineage is data rather than
 * prose.
 *
 * IMPORTANT — an Immersion is NOT a panel. An Immersion is the DAO starter kit
 * NFT that initialises an instance of this chassis (`src/lib/immersion.ts`,
 * ERC-6551). A panel fork is what a starter kit ships with: the School starter
 * kit ships the School panel. The two words are not interchangeable.
 *
 * A panel with `designed: false` has no token block of its own yet. That is
 * deliberate and honest: it inherits its parent's tokens (there is no
 * `[data-skin='…']` block, so `:root` plus the parent supplies everything),
 * which renders as an undifferentiated fork rather than a broken one.
 * Differentiating a panel means adding a token block, and nothing else.
 * ========================================================================= */

import { useCallback, useEffect, useState } from 'react';

export const SKINS = [
  'oceanside',
  'supercompute',
  'poolside',
  'school',
  'community-club',
  'athletic-club',
  'world-market',
  'poolside-web3',
  'idea-club',
  'oceanside-os',
] as const;

export type Skin = (typeof SKINS)[number];

export type SkinRole = 'master' | 'fork' | 'structural' | 'legacy';

/**
 * How an Immersion got its name. We sell the NFT; the name is either bought
 * through us (`issued`) or the operator already holds it with ENS (`brought`).
 * The Immersion builder sets this per buyer — it is data, not a token.
 */
export type NameProvenance = 'issued' | 'brought';

export const NAME_PROVENANCE_LABEL: Record<NameProvenance, string> = {
  issued: 'issued by us',
  brought: 'operator’s own ENS',
};

export interface SkinDef {
  id: Skin;
  label: string;
  role: SkinRole;
  /** The panel this one derives from. Every panel derives from the master. */
  forksFrom?: Skin;
  /** False ⇒ no token block yet; renders as its parent. */
  designed: boolean;
  /** Wordmark the desk wallpaper and boot plate carry for this panel. */
  wordmark: string;
  /**
   * Brand mark for the boot plate and dock. Which mark an Immersion wears is the
   * FRANCHISEE's call — the Franchisee role holds the charter and owns the
   * community's branding, so this is their field, not ours. Every panel currently
   * points at the Supercompute mark because no community has supplied its own
   * yet; nothing is invented in its place.
   */
  mark: string;
  /** The name on the wallpaper — data, not a token. */
  handle: string;
  nameProvenance: NameProvenance;
  note: string;
  /** Franchise message — shown on the boot plate under the handle. */
  tagline?: string;
}

const DEFAULT_MARK = '/logos/supercompute-mark-128.png';

export const SKIN_DEFS: readonly SkinDef[] = [
  {
    id: 'oceanside',
    label: 'OceanSide',
    role: 'master',
    designed: true,
    wordmark: 'OCEANSIDE',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'The master — the styleless chassis (Nude Build). Structure only; nothing to style. Every panel is a styled fork of this.',
  },
  {
    id: 'supercompute',
    label: 'Supercompute',
    role: 'fork',
    forksFrom: 'oceanside',
    designed: true,
    wordmark: 'SUPERCOMPUTE',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'Terminal Dossier chrome — navy, brass-gold, mono. The panel the umbrella ships with.',
  },
  {
    id: 'poolside',
    label: 'Poolside',
    role: 'fork',
    forksFrom: 'oceanside',
    designed: true,
    wordmark: 'POOLSIDE',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'Retro-80s vacation — light ground, black ink, hot-pink live. Formerly `poolsuite`.',
  },
  {
    id: 'school',
    label: 'School',
    role: 'fork',
    forksFrom: 'oceanside',
    designed: true,
    wordmark: 'RIVERBANK SCHOOL',
    mark: '/pixel/owl.svg',
    tagline: 'Learn the rails · keep your keys · own your diploma',
    handle: 'RiverbankSchool.eth',
    nameProvenance: 'issued',
    note: 'Green ground, white text, gold accent. The Franchisee owns this panel’s branding.',
  },
  {
    id: 'community-club',
    label: 'Community Club',
    role: 'fork',
    forksFrom: 'oceanside',
    designed: true,
    wordmark: 'COMMUNITY CLUB',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'The community half of the old club-manager fork. Deep burnt-orange ground, white text, gold accent.',
  },
  {
    id: 'athletic-club',
    label: 'Athletic Club',
    role: 'fork',
    forksFrom: 'oceanside',
    designed: true,
    wordmark: 'ATHLETIC CLUB',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'The athletic half of the old club-manager fork. Night-field charcoal ground, track-crimson affordance.',
  },
  {
    id: 'world-market',
    label: 'World Market',
    role: 'fork',
    forksFrom: 'oceanside',
    designed: true,
    wordmark: 'WORLD MARKET',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'A distributed flea-market styled community (see supercompute-os/docs/world-market.md). Warm ochre, cream, amber.',
  },
  {
    id: 'poolside-web3',
    label: 'Poolside Web3',
    role: 'fork',
    forksFrom: 'poolside',
    designed: false,
    wordmark: 'POOLSIDE WEB3',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'Poolside with Web3 rails — not yet built. Inherits the Poolside panel until differentiated.',
  },
  {
    id: 'idea-club',
    label: 'Idea Club',
    role: 'fork',
    forksFrom: 'supercompute',
    designed: false,
    wordmark: 'IDEA CLUB',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'Not yet built. Inherits the Supercompute panel until differentiated.',
  },
  {
    id: 'oceanside-os',
    label: 'OCEANSIDE OS',
    role: 'structural',
    forksFrom: 'oceanside',
    designed: true,
    wordmark: 'OCEANSIDE OS',
    mark: DEFAULT_MARK,
    handle: 'supercompute.eth',
    nameProvenance: 'issued',
    note: 'Native macOS/Aqua — black menu bar, white windows with traffic lights, SF Pro, floating pill dock, 10px radii. A structural fork (DESIGN.md §2.1): the gated class that owns geometry, type and chrome.',
  },
] as const;

/** The panel an unconfigured surface boots into: the umbrella's own. */
export const DEFAULT_SKIN: Skin = 'supercompute';

const STORAGE_KEY = 'supercompute-webos:skin';

export const SKIN_LABEL: Record<Skin, string> = SKIN_DEFS.reduce(
  (acc, d) => ({ ...acc, [d.id]: d.label }),
  {} as Record<Skin, string>,
);

export const SKIN_NOTE: Record<Skin, string> = SKIN_DEFS.reduce(
  (acc, d) => ({ ...acc, [d.id]: d.note }),
  {} as Record<Skin, string>,
);

export function skinDef(id: Skin): SkinDef {
  return SKIN_DEFS.find((d) => d.id === id) ?? SKIN_DEFS[1];
}

/** Every panel that derives from `id`, directly. */
export function forksOf(id: Skin): SkinDef[] {
  return SKIN_DEFS.filter((d) => d.forksFrom === id);
}

export const ROLE_LABEL: Record<SkinRole, string> = {
  master: 'master',
  fork: 'fork',
  structural: 'structural',
  legacy: 'legacy',
};

function isSkin(v: string | null): v is Skin {
  return v !== null && (SKINS as readonly string[]).includes(v);
}

function readInitial(): Skin {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    // A panel resolves to itself; only an unknown value falls back to default.
    if (isSkin(v)) return v;
  } catch {
    /* private mode / storage disabled — fall through to the default panel */
  }
  return DEFAULT_SKIN;
}

export interface SkinController {
  skin: Skin;
  setSkin: (s: Skin) => void;
  /** Cycle to the next panel in SKINS order. */
  cycle: () => void;
}

export function useSkin(): SkinController {
  const [skin, setSkinState] = useState<Skin>(readInitial);

  useEffect(() => {
    document.documentElement.setAttribute('data-skin', skin);
    try {
      localStorage.setItem(STORAGE_KEY, skin);
    } catch {
      /* non-fatal: the panel still applies for this session */
    }
  }, [skin]);

  const setSkin = useCallback((s: Skin) => setSkinState(s), []);

  const cycle = useCallback(
    () =>
      setSkinState((cur) => {
        const i = SKINS.indexOf(cur);
        return SKINS[(i + 1) % SKINS.length];
      }),
    [],
  );

  return { skin, setSkin, cycle };
}
